import React, { useState, useEffect } from 'react';
import { 
  Users, 
  ShoppingCart, 
  Plus, 
  Minus, 
  Trash2, 
  ThumbsUp, 
  ThumbsDown, 
  MessageCircle,
  Share2,
  Settings,
  Clock,
  DollarSign,
  Eye,
  EyeOff,
  Wifi,
  WifiOff
} from 'lucide-react';
import { useSharedCart } from './SharedCartProvider';
import { useUserStore } from '@/stores/userStore';
import { formatCurrency, formatRelativeTime } from '@/utils/formatting';
import { ProductDiscoveryInterface } from '@/components/product-discovery/ProductDiscoveryInterface';
import { ChatInterface } from '@/components/chat/ChatInterface';
import { KanbanBoard } from '@/components/kanban/KanbanBoard';

interface SharedCartInterfaceProps {
  cartId?: string;
  vendorId?: string;
  className?: string;
}

export const SharedCartInterface: React.FC<SharedCartInterfaceProps> = ({
  cartId,
  vendorId,
  className = '',
}) => {
  const {
    sharedCart,
    isConnected,
    isLoading,
    error,
    onlineParticipants,
    joinSharedCart,
    addItem,
    removeItem,
    updateItemQuantity,
    voteOnItem,
  } = useSharedCart();

  const [activeTab, setActiveTab] = useState<'cart' | 'kanban' | 'discovery' | 'chat' | 'participants' | 'activity'>('cart');
  const [showVotingPanel, setShowVotingPanel] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  // Join cart on mount if cartId is provided
  useEffect(() => {
    if (cartId && !sharedCart) {
      const { user } = useUserStore.getState();
      joinSharedCart(cartId, user?.displayName || 'Guest', !user);
    }
  }, [cartId, sharedCart, joinSharedCart]);

  if (isLoading) {
    return (
      <div className={`shared-cart-interface ${className}`}>
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading shared cart...</span>
        </div>
      </div>
    );
  }

  // Only a session that never loaded is a connection failure; a rejected
  // action is reported where it was taken.
  if (error && !sharedCart) {
    return (
      <div className={`shared-cart-interface ${className}`}>
        <div className="flex items-center justify-center p-8">
          <div className="text-center">
            <div className="text-red-500 mb-2">⚠️ Connection Error</div>
            <p className="text-gray-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!sharedCart) {
    return (
      <div className={`shared-cart-interface ${className}`}>
        <div className="flex items-center justify-center p-8">
          <div className="text-center">
            <ShoppingCart className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">No shared cart loaded</p>
          </div>
        </div>
      </div>
    );
  }

  const activeItems = sharedCart.items.filter(item => item.status === 'active');
  const totalValue = activeItems.reduce((sum, item) => sum + item.totalPrice, 0);
  const totalItems = activeItems.reduce((sum, item) => sum + item.quantity, 0);
  const activeParticipants = sharedCart.participants.filter(p => p.status === 'active');

  const handleQuantityChange = async (itemId: string, newQuantity: number) => {
    try {
      if (newQuantity <= 0) {
        await removeItem(itemId);
      } else {
        await updateItemQuantity(itemId, newQuantity);
      }
    } catch (error) {
      console.error('Failed to update quantity:', error);
    }
  };

  const handleVote = async (itemId: string, voteType: 'upvote' | 'downvote') => {
    try {
      await voteOnItem(itemId, voteType);
      setSelectedItemId(null);
    } catch (error) {
      console.error('Failed to vote:', error);
    }
  };

  return (
    <div className={`shared-cart-interface bg-white rounded-lg shadow-lg ${className}`}>
      {/* Header */}
      <div className="border-b border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShoppingCart className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {sharedCart.sessionName}
              </h2>
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  {activeParticipants.length} participants
                </span>
                <span className="flex items-center gap-1">
                  {isConnected ? (
                    <>
                      <Wifi className="w-4 h-4 text-green-500" />
                      Connected
                    </>
                  ) : (
                    <>
                      <WifiOff className="w-4 h-4 text-red-500" />
                      Disconnected
                    </>
                  )}
                </span>
                {sharedCart.expiresAt && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    Expires {formatRelativeTime(new Date(sharedCart.expiresAt))}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowVotingPanel(!showVotingPanel)}
              className={`p-2 rounded-lg transition-colors ${
                showVotingPanel ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              title={showVotingPanel ? 'Hide voting' : 'Show voting'}
            >
              {showVotingPanel ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
            <button className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors">
              <Share2 className="w-4 h-4" />
            </button>
            <button className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors">
              <Settings className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mt-4">
          {(['cart', 'kanban', 'discovery', 'chat', 'participants', 'activity'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab
                  ? 'bg-blue-100 text-blue-600'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {activeTab === 'cart' && (
          <div>
            {/* Cart Summary */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-gray-900">{totalItems}</div>
                  <div className="text-sm text-gray-600">Items</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {formatCurrency(totalValue, sharedCart.items[0]?.currency || 'USD')}
                  </div>
                  <div className="text-sm text-gray-600">Total</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">{activeParticipants.length}</div>
                  <div className="text-sm text-gray-600">People</div>
                </div>
              </div>
            </div>

            {/* Cart Items */}
            <div className="space-y-3">
              {activeItems.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No items in cart yet</p>
                  <p className="text-sm">Start shopping together!</p>
                </div>
              ) : (
                activeItems.map((item) => (
                  <CartItemCard
                    key={item.id}
                    item={item}
                    showVoting={showVotingPanel}
                    onQuantityChange={(newQuantity) => handleQuantityChange(item.id, newQuantity)}
                    onRemove={() => removeItem(item.id)}
                    onVote={(voteType) => handleVote(item.id, voteType)}
                    participants={activeParticipants}
                  />
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'kanban' && (
          <div className="min-h-96">
            <KanbanBoard
              sessionId={sharedCart.id}
              items={activeItems}
              participants={activeParticipants}
              className="h-full"
            />
          </div>
        )}

        {activeTab === 'discovery' && (
          <div className="min-h-96">
            <ProductDiscoveryInterface
              vendorId={vendorId || sharedCart.vendorId}
              sessionId={sharedCart.id}
              className="h-full"
            />
          </div>
        )}

        {activeTab === 'chat' && (
          <div className="min-h-96">
            <ChatInterface
              sessionId={sharedCart.id}
              className="h-full"
            />
          </div>
        )}

        {activeTab === 'participants' && (
          <div className="space-y-3">
            {activeParticipants.map((participant) => (
              <ParticipantCard
                key={participant.id}
                participant={participant}
                isOnline={onlineParticipants.has(participant.userId || participant.guestId || '')}
              />
            ))}
          </div>
        )}

        {activeTab === 'activity' && (
          <div className="space-y-3">
            <ActivityFeed cartId={sharedCart.id} />
          </div>
        )}
      </div>

      {/* Footer Actions */}
      {activeTab === 'cart' && (
        <div className="border-t border-line p-4">
          <button onClick={() => setActiveTab('discovery')} className="btn-quiet w-full">
            Keep shopping
          </button>
        </div>
      )}
    </div>
  );
};

// Cart Item Card Component
interface CartItemCardProps {
  item: any;
  showVoting: boolean;
  onQuantityChange: (quantity: number) => void;
  onRemove: () => void;
  onVote: (voteType: 'upvote' | 'downvote') => void;
  participants: any[];
}

const CartItemCard: React.FC<CartItemCardProps> = ({
  item,
  showVoting,
  onQuantityChange,
  onRemove,
  onVote,
  participants,
}) => {
  const [showVotePanel, setShowVotePanel] = useState(false);
  
  const addedBy = participants.find(p => 
    p.userId === item.addedByUserId || p.guestId === item.addedByGuestId
  );

  const upvotes = item.votes?.filter((v: any) => v.vote === 'upvote').length || 0;
  const downvotes = item.votes?.filter((v: any) => v.vote === 'downvote').length || 0;

  return (
    <div className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
      <div className="flex gap-4">
        {/* Product Image */}
        {item.product?.images?.[0] && (
          <div className="flex-shrink-0">
            <img
              src={item.product.images[0]}
              alt={item.product.title}
              className="w-16 h-16 rounded-lg object-cover"
            />
          </div>
        )}

        {/* Product Details */}
        <div className="flex-1">
          <div className="flex items-start justify-between mb-2">
            <div>
              <h3 className="font-medium text-gray-900">{item.product?.title}</h3>
              <p className="text-sm text-gray-600">
                Added by {addedBy?.displayName || 'Someone'}
              </p>
              {item.notes && (
                <p className="text-sm text-gray-500 italic mt-1">"{item.notes}"</p>
              )}
            </div>
            <div className="text-right">
              <div className="font-semibold text-gray-900">
                {formatCurrency(item.totalPrice, item.currency)}
              </div>
              <div className="text-sm text-gray-600">
                {formatCurrency(item.unitPrice, item.currency)} each
              </div>
            </div>
          </div>

          {/* Quantity Controls */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center border border-gray-300 rounded-lg">
                <button
                  onClick={() => onQuantityChange(item.quantity - 1)}
                  className="p-1 hover:bg-gray-100 transition-colors"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="px-3 py-1 text-center min-w-[3rem]">{item.quantity}</span>
                <button
                  onClick={() => onQuantityChange(item.quantity + 1)}
                  className="p-1 hover:bg-gray-100 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>

              <button
                onClick={onRemove}
                className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                title="Remove item"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>

            {/* Voting Controls */}
            {showVoting && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onVote('upvote')}
                  className={`flex items-center gap-1 px-2 py-1 rounded text-sm transition-colors ${
                    upvotes > 0 ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600 hover:bg-green-100'
                  }`}
                >
                  <ThumbsUp className="w-3 h-3" />
                  {upvotes > 0 && upvotes}
                </button>
                <button
                  onClick={() => onVote('downvote')}
                  className={`flex items-center gap-1 px-2 py-1 rounded text-sm transition-colors ${
                    downvotes > 0 ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600 hover:bg-red-100'
                  }`}
                >
                  <ThumbsDown className="w-3 h-3" />
                  {downvotes > 0 && downvotes}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Participant Card Component
interface ParticipantCardProps {
  participant: any;
  isOnline: boolean;
}

const ParticipantCard: React.FC<ParticipantCardProps> = ({ participant, isOnline }) => {
  return (
    <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
      <div className="flex items-center gap-3">
        <div className="relative">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
            {participant.displayName.charAt(0).toUpperCase()}
          </div>
          <div 
            className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white ${
              isOnline ? 'bg-green-400' : 'bg-gray-400'
            }`}
          />
        </div>
        
        <div>
          <div className="font-medium text-gray-900">{participant.displayName}</div>
          <div className="text-sm text-gray-600 capitalize">{participant.role}</div>
        </div>
      </div>

      <div className="text-sm text-gray-500">
        {isOnline ? 'Online' : 'Offline'}
      </div>
    </div>
  );
};

// Activity Feed Component
interface ActivityFeedProps {
  cartId: string;
}

const ActivityFeed: React.FC<ActivityFeedProps> = ({ cartId }) => {
  const [activities, setActivities] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        // This would be an API call to get cart activities
        // For now, showing placeholder
        setActivities([
          {
            id: '1',
            type: 'item_added',
            description: 'Added iPhone 15 Pro to cart',
            user: { displayName: 'Alice' },
            createdAt: new Date().toISOString(),
          },
          {
            id: '2',
            type: 'user_joined',
            description: 'Bob joined the session',
            user: { displayName: 'Bob' },
            createdAt: new Date(Date.now() - 300000).toISOString(),
          },
        ]);
      } catch (error) {
        console.error('Failed to fetch activities:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchActivities();
  }, [cartId]);

  if (isLoading) {
    return <div className="text-center py-4 text-gray-500">Loading activities...</div>;
  }

  return (
    <div className="space-y-3">
      {activities.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No activity yet</p>
        </div>
      ) : (
        activities.map((activity) => (
          <div key={activity.id} className="flex gap-3 p-3 border border-gray-200 rounded-lg">
            <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <div className="w-2 h-2 bg-blue-600 rounded-full" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-900">{activity.description}</p>
              <p className="text-xs text-gray-500">
                {formatRelativeTime(new Date(activity.createdAt))}
              </p>
            </div>
          </div>
        ))
      )}
    </div>
  );
};